import {useParams, useNavigate} from "react-router-dom";
import {Alert, Tag} from "antd";
import className from "classnames/bind";
import {FontAwesomeIcon, Loading, PageHeader, ProjectQuota, ProjectToolNav} from "~/components";
import {useCan} from "~/hooks";
import {useGetProjectQuery} from "~/reduxs/api/projectApiSlice";
import {useGetMySubscriptionQuery} from "~/reduxs/api/subscriptionApiSlice";
import ProjectOverview from "../components/ProjectOverview";
import ProjectChecklist from "../components/ProjectChecklist";
import ReportScheduleCard from "../components/ReportScheduleCard";
import style from "../style/Project.module.scss";

const cn = className.bind(style);

/**
 * Dashboard của một Dự án: thông tin website + lưới CÔNG CỤ SEO bên trong dự án.
 * Site Crawler đang hoạt động; các tool khác (Google Search Console...) hiển thị "Sắp có".
 */
function ProjectDashboard() {

	const {id} = useParams();
	const navigate = useNavigate();
	const canCrawl = useCan('crawl');
	const canGsc = useCan('gsc');
	const canGa = useCan('ga');
	const canAi = useCan('ai');
	const canKeyword = useCan('keyword');
	const canCompetitor = useCan('competitor');

	const {data: project, isLoading} = useGetProjectQuery(id);
	const {data: sub} = useGetMySubscriptionQuery();

	// Gói "Riêng (khoá AI riêng)": tắt các công cụ tra cứu dữ liệu nâng cao (thứ hạng,
	// backlink, organic đối thủ) — làm mờ thẻ, không cho mở (BE cũng chặn theo hạn mức 0).
	const advancedBlocked = !sub?.is_root && sub?.plan?.slug === 'private';

	if (isLoading) return <Loading />;
	if (!project) return <div className="container">Không tìm thấy dự án.</div>;

	// Danh sách công cụ của dự án (mở rộng dần khi thêm tool mới).
	const tools = [
		{
			key: 'crawler',
			name: 'Site Crawler',
			desc: 'Crawl toàn site, chấm điểm SEO từng trang + phát hiện lỗi cấp site (duplicate, broken link, orphan...).',
			icon: 'fa-light fa-spider',
			color: '#4f46e5',
			available: true,
			allowed: canCrawl,
			to: `/projects/${project.id}/crawler`,
		},
		{
			key: 'technical',
			name: 'Kỹ thuật SEO',
			desc: 'Báo cáo kỹ thuật từ lần crawl mới nhất: liên kết nội bộ & cơ hội liên kết, liên kết hỏng, chuyển hướng, robots.txt.',
			icon: 'fa-light fa-screwdriver-wrench',
			color: '#0891b2',
			available: true,
			allowed: canCrawl,
			to: `/projects/${project.id}/technical`,
		},
		{
			key: 'gsc',
			name: 'Google Search Console',
			desc: 'Kết nối GSC để theo dõi hiệu suất tìm kiếm, click/impression, từ khoá, lập chỉ mục.',
			icon: 'fa-light fa-magnifying-glass-chart',
			color: '#0ea5e9',
			available: true,
			allowed: canGsc,
			to: `/projects/${project.id}/gsc`,
		},
		{
			key: 'analytics',
			name: 'Google Analytics 4',
			desc: 'Kết nối GA4 để theo dõi lưu lượng thật: phiên, người dùng, kênh, nguồn, sự kiện chính, doanh thu.',
			icon: 'fa-light fa-chart-mixed',
			color: '#e97316',
			available: true,
			allowed: canGa,
			to: `/projects/${project.id}/analytics`,
		},
		{
			key: 'keywords',
			name: 'Nghiên cứu từ khoá',
			desc: 'Khám phá từ khoá từ dữ liệu Search Console thật (impressions, vị trí), lưu kho từ khoá của dự án.',
			icon: 'fa-light fa-tags',
			color: '#16a34a',
			available: true,
			allowed: canKeyword,
			to: `/projects/${project.id}/keywords`,
		},
		{
			key: 'rank',
			name: 'Theo dõi thứ hạng',
			desc: 'Vị trí THẬT trên Google theo Desktop/Mobile + quốc gia, diễn biến theo ngày, đối thủ cùng trang & SERP features.',
			icon: 'fa-light fa-ranking-star',
			color: '#ca8a04',
			available: true,
			allowed: canKeyword,
			planBlocked: advancedBlocked,
			to: `/projects/${project.id}/rank`,
		},
		{
			key: 'backlinks',
			name: 'Backlink',
			desc: 'Tổng backlink, domain giới thiệu, anchor text, dofollow/nofollow và diễn biến mới/mất theo tháng.',
			icon: 'fa-light fa-link',
			color: '#9333ea',
			available: true,
			allowed: canKeyword,
			planBlocked: advancedBlocked,
			to: `/projects/${project.id}/backlinks`,
		},
		{
			key: 'competitor',
			name: 'Phân tích đối thủ',
			desc: 'So sánh điểm SEO on-page với đối thủ, content gap nhiều đối thủ và ma trận độ phủ từ khoá từ GSC.',
			icon: 'fa-light fa-users-viewfinder',
			color: '#db2777',
			available: true,
			allowed: canCompetitor,
			to: `/projects/${project.id}/competitor`,
		},
		{
			key: 'ai',
			name: 'AI SEO',
			desc: 'Trợ lý AI phân tích website dựa trên dữ liệu crawl/SEO/GSC + sinh nội dung (meta, FAQ, dàn ý).',
			icon: 'fa-light fa-wand-magic-sparkles',
			color: '#7c3aed',
			available: true,
			allowed: canAi,
			to: `/projects/${project.id}/ai`,
		},
		{
			key: 'content',
			name: 'Nội dung AI',
			desc: 'AI tự lên dàn ý theo search intent thật (top 10 + PAA + AI Overview), viết bài chuẩn SEO đúng thương hiệu kèm ảnh minh hoạ.',
			icon: 'fa-light fa-feather-pointed',
			color: '#db2777',
			available: true,
			allowed: canAi,
			to: `/projects/${project.id}/content`,
		},
	];

	return (
		<div className="container">
			<ProjectToolNav projectId={project.id} active="overview" />

			<PageHeader
				icon="fa-light fa-folder-open"
				title={project.name}
				subtitle={project.root_url}
				actions={<ProjectQuota tool="overview" />}
			/>

			{/* Dự án chỉ đọc do vượt trần gói (mục 5.3): xem dữ liệu cũ được, chạy tool bị chặn. */}
			{project.locked && (
				<Alert type="warning" showIcon style={{marginBottom: 16}}
					message="Dự án đang ở chế độ chỉ đọc"
					description="Số dự án của bạn vượt hạn mức gói nên dự án này chỉ xem được dữ liệu/báo cáo cũ, không chạy được công cụ. Kích hoạt lại trong trang Dự án (nhường chỗ bằng cách khoá dự án khác) hoặc nâng cấp gói."
					action={<Tag color="orange">Chỉ đọc</Tag>} />
			)}

			<div className={cn('card')}>
				<div className={cn('infoRow')}>
					<div>Website: <strong>{project.root_url}</strong></div>
					<div>Domain: <strong>{project.domain}</strong></div>
				</div>
			</div>

			{/* Onboarding: hướng dẫn bước tiếp theo, tự ẩn khi đã dùng đủ các tool. */}
			<ProjectChecklist projectId={project.id} />

			{/* Tổng quan: gom số liệu từ các tool đã chạy (crawler/gsc/pagespeed/seo). */}
			<ProjectOverview projectId={project.id} />

			{/* Báo cáo email định kỳ (#22): tổng hợp số liệu dự án gửi vào hộp thư theo chu kỳ. */}
			<ReportScheduleCard projectId={project.id} />

			<h3 style={{fontSize: 15, fontWeight: 600, margin: '20px 0 10px'}}>Công cụ SEO</h3>
			<div className={cn('tools')}>
				{tools.map((t) => {
					const clickable = t.available && t.allowed && !t.planBlocked;
					return (
						<div
							key={t.key}
							className={cn('toolCard', {active: clickable, disabled: !clickable})}
							onClick={() => clickable && navigate(t.to)}
						>
							<div className={cn('toolIcon')} style={{background: t.color}}>
								<FontAwesomeIcon icon={t.icon} />
							</div>
							<div className={cn('cardHead')} style={{alignItems: 'center'}}>
								<h4 className={cn('toolName')}>{t.name}</h4>
								{!t.available && <Tag>Sắp có</Tag>}
								{t.available && !t.allowed && <Tag color="red">Không có quyền</Tag>}
								{t.available && t.allowed && t.planBlocked && <Tag>Không khả dụng với gói của bạn</Tag>}
							</div>
							<div className={cn('toolDesc')}>{t.desc}</div>
							{clickable && (
								<div style={{color: t.color, fontSize: 13, fontWeight: 500}}>
									Mở công cụ <FontAwesomeIcon icon="fa-light fa-arrow-right" />
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
}

export default ProjectDashboard;
